/**
 * useCvEditing
 *
 * Gathers all the CV field-level CRUD helpers that the <EditSidebar>
 * consumes. Previously these lived inline in App.tsx as 16 little
 * arrow functions; moving them behind a single hook keeps App.tsx
 * focused on orchestration and makes the editing surface reusable
 * for a future Phase 3 "quick" editor.
 *
 * The hook takes `setData` (from useCvState) and `setCommonInfo` /
 * `setFieldVisibility` (same) as params rather than calling
 * `useCvState()` itself, so that the caller decides the single
 * source of truth. It also takes `data` read-only for the skill /
 * language / hobby helpers which do a splice-like `data.xxx[idx]`
 * update before calling setData.
 *
 * The whole hook is intentionally side-effect free beyond the
 * setters it's given — it owns zero local state.
 */

import { useCallback, type Dispatch, type SetStateAction } from 'react';
import type { CVData, Experience, Education } from '../types';
import type { CommonInfo, FieldVisibility, TemplateMetaItem } from './useCvState';

/** Image compression helper used by the avatar upload flow. */
function compressImage(base64Str: string, maxWidth: number, quality: number): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image();
    img.src = base64Str;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const scale = maxWidth / img.width;
      canvas.width = maxWidth;
      canvas.height = img.height * scale;
      const ctx = canvas.getContext('2d');
      ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
  });
}

/** Fields that belong to the shared "common info" rather than the
 *  per-version CVData. Keep in sync with the keys in CommonInfo. */
export const COMMON_FIELDS = new Set([
  'fullName',
  'firstName',
  'lastName',
  'civility',
  'email',
  'phone',
  'location',
  'city',
  'country',
  'birthDate',
  'avatar',
  'nationality',
  'birthPlace',
  'gender',
  'maritalStatus',
]);

export interface UseCvEditingParams {
  data: CVData;
  setData: Dispatch<SetStateAction<CVData>>;
  setCommonInfo: Dispatch<SetStateAction<CommonInfo>>;
  setFieldVisibility: Dispatch<SetStateAction<FieldVisibility>>;
  activeTemplate: string;
  setTemplateMeta: Dispatch<SetStateAction<TemplateMetaItem[]>>;
}

export interface UseCvEditingReturn {
  updatePersonalInfo: (field: keyof CVData['personalInfo'], value: string) => void;
  toggleFieldVisibility: (field: keyof FieldVisibility) => void;
  handleImageUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** Commit a cropped avatar (standard + compressed variants) from PhotoCropModal. */
  setCroppedAvatar: (standard: string, compressed: string) => void;

  // Experience
  updateExperience: (index: number, field: keyof Experience, value: string) => void;
  addExperience: () => void;
  removeExperience: (index: number) => void;

  // Education
  updateEducation: (
    index: number,
    field: 'school' | 'degree' | 'date' | 'startDate' | 'endDate',
    value: string,
  ) => void;
  addEducation: () => void;
  removeEducation: (index: number) => void;

  // Skills / Languages / Hobbies (all string arrays)
  updateSkill: (index: number, value: string) => void;
  addSkill: () => void;
  removeSkill: (index: number) => void;
  updateLanguage: (index: number, value: string) => void;
  addLanguage: () => void;
  removeLanguage: (index: number) => void;
  updateHobby: (index: number, value: string) => void;
  addHobby: () => void;
  removeHobby: (index: number) => void;

  /** Renames the active version in templateMeta (synced from job title) */
  renameActiveVersion: (name: string) => void;
}

export function useCvEditing(params: UseCvEditingParams): UseCvEditingReturn {
  const { data, setData, setCommonInfo, setFieldVisibility, activeTemplate, setTemplateMeta } =
    params;

  // --- Personal info -----------------------------------------------------
  const updatePersonalInfo = useCallback(
    (field: keyof CVData['personalInfo'], value: string) => {
      if (COMMON_FIELDS.has(field as string)) {
        setCommonInfo((prev) => {
          const updated: CommonInfo = { ...prev, [field]: value };
          // Keep the legacy `location` field in sync when city or country
          // changes so older CV renderers still show a full address.
          if (field === 'city' || field === 'country') {
            const city = field === 'city' ? value : prev.city || '';
            const country = field === 'country' ? value : prev.country || '';
            updated.location = city && country
              ? `${city}, ${country}`
              : city || country || prev.location || '';
          }
          return updated;
        });
      } else {
        setData((prev) => ({
          ...prev,
          personalInfo: { ...prev.personalInfo, [field]: value },
        }));
      }
    },
    [setCommonInfo, setData],
  );

  const toggleFieldVisibility = useCallback(
    (field: keyof FieldVisibility) => {
      setFieldVisibility((prev) => ({ ...prev, [field]: !prev[field] }));
    },
    [setFieldVisibility],
  );

  const handleImageUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      // Guard : rejeter les fichiers > 5 MB avant de lire en mémoire
      const MAX_FILE_BYTES = 5 * 1024 * 1024;
      if (file.size > MAX_FILE_BYTES) {
        window.dispatchEvent(new CustomEvent("avatar-upload-error", {
          detail: { message: "Image trop volumineuse (max 5 Mo). Choisissez une image plus petite." },
        }));
        e.target.value = "";
        return;
      }
      if (!file.type.startsWith("image/")) {
        e.target.value = "";
        return;
      }

      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result as string;
        // Emit the raw base64 for any listener (e.g. crop modal) BEFORE committing.
        // If a listener calls preventDefault, it takes ownership of the crop step.
        const ev = new CustomEvent("avatar-file-loaded", { detail: { base64 }, cancelable: true });
        const consumedByCropper = !window.dispatchEvent(ev);
        if (consumedByCropper) return;
        const standardAvatar = await compressImage(base64, 800, 0.8);
        const lightAvatar = await compressImage(base64, 300, 0.5);
        setCommonInfo((prev) => ({
          ...prev,
          avatar: standardAvatar,
          compressedAvatar: lightAvatar,
        }));
      };
      reader.readAsDataURL(file);
    },
    [setCommonInfo],
  );

  const setCroppedAvatar = useCallback(
    (standard: string, compressed: string) => {
      setCommonInfo((prev) => ({
        ...prev,
        avatar: standard,
        compressedAvatar: compressed,
      }));
    },
    [setCommonInfo],
  );

  // --- Experience --------------------------------------------------------
  const updateExperience = useCallback(
    (index: number, field: keyof Experience, value: string) => {
      setData((prev) => {
        const newExp = [...prev.experience];
        newExp[index] = { ...newExp[index], [field]: value } as Experience;
        return { ...prev, experience: newExp };
      });
    },
    [setData],
  );

  const addExperience = useCallback(() => {
    setData((prev) => ({
      ...prev,
      experience: [
        { company: '', position: '', startDate: '', endDate: '', description: '' },
        ...prev.experience,
      ],
    }));
  }, [setData]);

  const removeExperience = useCallback(
    (index: number) => {
      setData((prev) => ({
        ...prev,
        experience: prev.experience.filter((_, i) => i !== index),
      }));
    },
    [setData],
  );

  // --- Education ---------------------------------------------------------
  const updateEducation = useCallback(
    (
      index: number,
      field: 'school' | 'degree' | 'date' | 'startDate' | 'endDate',
      value: string,
    ) => {
      setData((prev) => {
        const newEdu = [...prev.education];
        const current = newEdu[index];
        if (current) {
          newEdu[index] = { ...current, [field]: value } as Education;
        }
        return { ...prev, education: newEdu };
      });
    },
    [setData],
  );

  const addEducation = useCallback(() => {
    setData((prev) => ({
      ...prev,
      education: [
        ...prev.education,
        { school: '', degree: '', date: '', startDate: '', endDate: '' },
      ],
    }));
  }, [setData]);

  const removeEducation = useCallback(
    (index: number) => {
      setData((prev) => ({
        ...prev,
        education: prev.education.filter((_, i) => i !== index),
      }));
    },
    [setData],
  );

  // --- Simple string-array sections (skills / languages / hobbies) -------
  // These read the current array from `data` then splice + save. They'd
  // be safer with a functional setData, but we keep the historical
  // behavior to avoid any race condition with the editor's onChange
  // handlers fired in rapid succession.

  const updateSkill = useCallback(
    (index: number, value: string) => {
      const newSkills = [...data.skills];
      newSkills[index] = value;
      setData((prev) => ({ ...prev, skills: newSkills }));
    },
    [data.skills, setData],
  );

  const addSkill = useCallback(() => {
    setData((prev) => ({ ...prev, skills: [...prev.skills, 'Skill'] }));
  }, [setData]);

  const removeSkill = useCallback(
    (index: number) => {
      setData((prev) => ({
        ...prev,
        skills: prev.skills.filter((_, i) => i !== index),
      }));
    },
    [setData],
  );

  const updateLanguage = useCallback(
    (index: number, value: string) => {
      const newLangs = [...data.languages];
      newLangs[index] = value;
      setData((prev) => ({ ...prev, languages: newLangs }));
    },
    [data.languages, setData],
  );

  const addLanguage = useCallback(() => {
    setData((prev) => ({ ...prev, languages: [...prev.languages, 'Langue'] }));
  }, [setData]);

  const removeLanguage = useCallback(
    (index: number) => {
      setData((prev) => ({
        ...prev,
        languages: prev.languages.filter((_, i) => i !== index),
      }));
    },
    [setData],
  );

  const updateHobby = useCallback(
    (index: number, value: string) => {
      const newHobbies = [...data.hobbies];
      newHobbies[index] = value;
      setData((prev) => ({ ...prev, hobbies: newHobbies }));
    },
    [data.hobbies, setData],
  );

  const addHobby = useCallback(() => {
    setData((prev) => ({
      ...prev,
      hobbies: [...prev.hobbies, 'Loisir'],
    }));
  }, [setData]);

  const removeHobby = useCallback(
    (index: number) => {
      setData((prev) => ({
        ...prev,
        hobbies: prev.hobbies.filter((_, i) => i !== index),
      }));
    },
    [setData],
  );

  const renameActiveVersion = useCallback(
    (name: string) => {
      if (!name.trim()) return;
      setTemplateMeta((prev) =>
        prev.map((tm) => (tm.id === activeTemplate ? { ...tm, name: name.trim() } : tm)),
      );
    },
    [activeTemplate, setTemplateMeta],
  );

  return {
    updatePersonalInfo,
    toggleFieldVisibility,
    handleImageUpload,
    setCroppedAvatar,
    updateExperience,
    addExperience,
    removeExperience,
    updateEducation,
    addEducation,
    removeEducation,
    updateSkill,
    addSkill,
    removeSkill,
    updateLanguage,
    addLanguage,
    removeLanguage,
    updateHobby,
    addHobby,
    removeHobby,
    renameActiveVersion,
  };
}
